import React, { useState, useEffect, useRef, useContext } from 'react';
import { UserProfile, Briefcase, DocumentEdit } from '@sravni/react-icons';
import { Paper } from '../../../../../landing.common';
import { StepWrapper } from '../step.wrapper';
import { Step1 } from './components/step1';
import { Step2 } from './components/step2';
import { Step3 } from './components/step3';
import { Step4 } from './components/step4';
import { useAutoFill } from '../../../../../form/hooks/autofill';
import { useProfile } from '../../../../../form/hooks/profile';
import { FormProgress } from '../form.progress';
import styles from './styles.module.scss';
import { useGetLimits } from '../../../../../hooks/getLimits';
import { FORM_FIELDS } from '../../../../../../constants';
import analytics from '../../../../../../helpers/analytics';
import { scrollTo } from '../../../../../../helpers';
import { extendFormData, extendFormStore } from '../../../../../../store/reducers/formStore';
import { useFormFields, useFormStore } from '../../../../../../store/selectors/formStore';
import { useLocal } from '../../../../../form/hooks/local';
import { useQueryData } from '../../../../../form/hooks/queryData';
import { useWhitelabelLocal } from '../../../../../form/hooks/wl.local';
import { useDispatch } from 'react-redux';
import { MainSkeleton } from '../../../../../landing.common/main.skeleton';
import { useMainSkeleton } from '../../../../../../store/reducers/selection';
import { CardsContext } from '../../../../context';
import { useRouteLoader } from '../../../../../form/hooks/routeLoader';
import { useAddressNormalizer } from '../../../../../form/hooks/addressLoader';

const STEP_COUNT = 4;

type Props = {
    onFormPassed: () => void;
};

export const FormSteps: React.FC<Props> = ({ onFormPassed }) => {
    const { withSilentAuth, autoRun, holdOn } = useFormStore();
    const { mainSkeletonIsActive } = useMainSkeleton();
    const dispatch = useDispatch();
    const { taskManager } = useContext(CardsContext);
    const [okb, salary] = useFormFields([FORM_FIELDS.OKB_SCORE, FORM_FIELDS.MONTHLY_SALARY]);

    const stepRefs = useRef<Array<HTMLDivElement | null>>(Array(STEP_COUNT).fill(null));

    const { inProcess, amount, getLimits } = useGetLimits();

    const [step, setStep] = useState(1);
    const [mounted, setMounted] = useState(false);

    useAutoFill(true);
    useLocal(true);
    useQueryData(true);
    useWhitelabelLocal(withSilentAuth);
    // На странице подбора кредитных карт не сохраняем сумму, цель и период кредита в profile, чтобы они не подставлялись в подборе
    useProfile(true, [FORM_FIELDS.AMOUNT, FORM_FIELDS.PURPOSE, FORM_FIELDS.PERIOD]);
    useRouteLoader(taskManager);
    useAddressNormalizer(taskManager);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        const monthlyIncome = Number(salary) || 0;
        getLimits({ monthlyIncome, okb }, step);
    }, [step, okb, salary]);

    useEffect(() => {
        dispatch(extendFormData({ [FORM_FIELDS.AMOUNT]: amount }));
    }, [amount, dispatch]);

    const onNext = () => {
        const safeStep = Math.min(step + 1, STEP_COUNT);
        setStep(safeStep);
    };

    const onGoToStep = (step: number) => {
        const safeStep = Math.max(Math.min(step, STEP_COUNT), 1);
        analytics.emit({
            event: analytics.events.adEvent,
            eventCategory: analytics.categories.CreditCard,
            eventAction: `Шаг ${safeStep}|Параметры|Шаг Назад`,
        });
        setStep(safeStep);
    };

    useEffect(() => {
        if (!mainSkeletonIsActive) {
            scrollTo(stepRefs.current[step]);
        }
    }, [mainSkeletonIsActive, step]);

    const onManualGoToStep = (step: number) => {
        dispatch(extendFormStore({ autoRun: false }));
        onGoToStep(step);
    };

    const onFinalStep = () => {
        onFormPassed();
        dispatch(extendFormStore({ autoRun: false }));
    };

    if (!mounted) {
        return <MainSkeleton />;
    }

    return (
        <div className={styles.root}>
            {(autoRun || holdOn) && <MainSkeleton />}
            <Paper className={styles.paper}>
                <StepWrapper
                    ref={(step) => (stepRefs.current[0] = step)}
                    title="Шаг 1"
                    subTitle="Контакты"
                    opened={step === 1}
                    completed={step > 1}
                    onClickOpen={() => onManualGoToStep(1)}>
                    <Step1 onNext={onNext} />
                </StepWrapper>
                <StepWrapper
                    ref={(step) => (stepRefs.current[1] = step)}
                    title="Шаг 2"
                    subTitle="Паспортные данные"
                    opened={step === 2}
                    completed={step > 2}
                    onClickOpen={() => onManualGoToStep(2)}
                    imgIcon={<UserProfile />}>
                    <Step2 onNext={onNext} />
                </StepWrapper>
                <StepWrapper
                    ref={(step) => (stepRefs.current[2] = step)}
                    title="Шаг 3"
                    subTitle="Место работы"
                    opened={step === 3}
                    completed={step > 3}
                    onClickOpen={() => onManualGoToStep(3)}
                    imgIcon={<Briefcase />}>
                    <Step3 onNext={onNext} />
                </StepWrapper>
                <StepWrapper
                    ref={(step) => (stepRefs.current[3] = step)}
                    title="Шаг 4"
                    subTitle="Дополнительно"
                    opened={step === 4}
                    imgIcon={<DocumentEdit />}>
                    <Step4 onNext={onFinalStep} />
                </StepWrapper>
            </Paper>
            <FormProgress step={step} inProcess={inProcess} />
        </div>
    );
};
